import React, { useMemo, useReducer, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppSelector } from 'utils/hooks';

import photoModule from 'domains/photos';

import Button from 'components/shared/Button';
import Modal from 'components/shared/Modal';

import type {
  NamedCollection,
  PendingCollectionAction,
  UncollectedCollection,
} from 'domains/photos';

type CreateNewCollectionModalProps = {
  uncollected: UncollectedCollection;
  collections: NamedCollection[];
  onCancel: () => void;
  onConfirm: (name: string, action: PendingCollectionAction, donorIds?: number[]) => void;
};

function CreateNewCollectionModal({
  uncollected,
  collections,
  onCancel,
  onConfirm,
}: CreateNewCollectionModalProps) {
  const [name, setName] = useState('');
  const [createAction, setCreateAction] = useState<PendingCollectionAction>(() =>
    uncollected.count > 0 ? 'create_from_orphans' : 'create_from_upload'
  );
  const [donorCollection, setDonorCollection] = useState<string>();
  const [showDonorPhotos, setShowDonorPhotos] = useState(false);
  const [selectedDonorPhotos, setSelectedDonorPhotos] = useReducer(
    (state: number[], payload: { id?: number; type: 'set' | 'reset' }) => {
      switch (payload.type) {
        case 'reset':
          return [];
        case 'set':
          if (!payload.id) return state;
          if (state.includes(payload.id)) {
            return state.filter((i) => i !== payload.id);
          } else {
            return [...state, payload.id];
          }
      }
    },
    []
  );

  // Would rather not use redux directly in this component, but I also don't
  // want to keep all photos in the parent component's state just for the possibility of
  // filtering here. Better solution is likely, just don't know it right now
  const donorPhotos = useAppSelector((state) =>
    photoModule.selectors.allForCollection(state, donorCollection)
  );

  const disableConfirm = useMemo(
    () =>
      name === '' ||
      !!collections.find((c) => c.name === name) ||
      (createAction === 'create_from_existing' && !donorCollection),
    [name, collections, createAction, donorCollection]
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (disableConfirm) return;

    if (donorCollection && selectedDonorPhotos.length === 0) {
      setShowDonorPhotos(true);
      return;
    }

    onConfirm(name, createAction, selectedDonorPhotos);
  }

  function handleCancel() {
    if (showDonorPhotos) {
      setShowDonorPhotos(false);
      setSelectedDonorPhotos({ type: 'reset' });
    } else {
      onCancel();
    }
  }

  function toggleDonorPhoto(id: number) {
    setSelectedDonorPhotos({ type: 'set', id });
  }

  const buttons = (
    <React.Fragment>
      <Button
        type="confirm"
        htmlType="submit"
        form="create-collection-form"
        disabled={disableConfirm}
      >
        Confirm
      </Button>
      <Button type="cancel" onClick={handleCancel}>
        Cancel
      </Button>
    </React.Fragment>
  );

  return (
    <Modal
      lastFocusedElementId="create-new-collection"
      labelledBy="create-collection-modal"
      modalClass={['create-collection-modal']}
      title="Create New Collection"
      primaryFooterGroup={buttons}
      onClose={onCancel}
    >
      <form id="create-collection-form" onSubmit={handleSubmit}>
        {!showDonorPhotos && (
          <React.Fragment>
            <label htmlFor="rename-collection">Collection Name:</label>
            <input id="rename-collection" onChange={(e) => setName(e.target.value)} value={name} />

            <label htmlFor="create-collection-source">Collect Images From:</label>
            <select
              id="create-collection-source"
              onChange={(e) => setCreateAction(e.target.value as PendingCollectionAction)}
              value={createAction}
            >
              {uncollected.count > 0 && (
                <option
                  value="create_from_orphans"
                  title="Create new collection from all uncollected images"
                >
                  {`Collect ${uncollected.count} Uncollected`}
                </option>
              )}
              <option value="create_from_upload" title="Upload images to create new collection">
                New From Upload
              </option>
              {collections.length > 0 && (
                <option
                  value="create_from_existing"
                  title="Select images from existing collection to create new collection"
                >
                  Pick From Existing
                </option>
              )}
            </select>
            {createAction === 'create_from_existing' && (
              // <Select
              //   isClearable={false}
              //   placeholder="Select a Collection"
              //   onChange={(v) => setDonorCollection(v?.value)}
              //   options={collections.map(({ name }) => ({ value: name, label: name }))}
              // />
              <select
                placeholder="select one"
                onChange={(e) => setDonorCollection(e.target.value)}
                value={donorCollection}
              >
                <option value="">Select a Collection</option>
                {collections.map(({ name }) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </React.Fragment>
        )}

        {showDonorPhotos && (
          <React.Fragment>
            <h2>Select Photos To Move</h2>
            <ul>
              {donorPhotos.map((p) => (
                <li className={selectedDonorPhotos.includes(p.id) ? 'selected' : ''}>
                  <label>
                    <img src={p.file_path_thumb} />
                    {p.original_filename}{' '}
                    <input
                      type="checkbox"
                      checked={selectedDonorPhotos.includes(p.id)}
                      className="screen-reader-text"
                      onChange={() => toggleDonorPhoto(p.id)}
                    />
                    {selectedDonorPhotos.includes(p.id) && (
                      <FontAwesomeIcon icon={['far', 'check-circle']} size="3x" />
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </React.Fragment>
        )}
      </form>
    </Modal>
  );
}

export default CreateNewCollectionModal;
